// PermissionContext - Gestion des permissions et feature flags
// Source unique de vérité pour l'autorisation côté frontend
// Utilise /api/platform/context qui est calculé par le même moteur que le backend

import { createContext, useContext, useState, useEffect, useCallback } from "react";

const API = process.env.REACT_APP_BACKEND_URL || "";

const PermissionContext = createContext(null);

export function PermissionProvider({ children, user, currentTenant }) {
  const [permissionContext, setPermissionContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Charger le contexte d'autorisation depuis le backend
  const loadPermissionContext = useCallback(async () => {
    // Extraire le tenant ID
    const tenantId = currentTenant?.slug || currentTenant?.id || currentTenant;
    
    if (!user?.email || !tenantId) {
      console.log("[PermissionContext] Pas de user ou tenant, skip load", { email: user?.email, tenantId });
      setPermissionContext(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log("[PermissionContext] Chargement permissions pour", { email: user.email, tenantId });
      
      const token = localStorage.getItem('session_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await fetch(
        `${API}/api/platform/context?user_email=${encodeURIComponent(user.email)}&tenant_id=${encodeURIComponent(tenantId)}`,
        { headers, credentials: "include" }
      );
      
      if (response.ok) {
        const data = await response.json();
        console.log("[PermissionContext] Permissions chargées:", { hubs: Object.keys(data.hubs || {}), role: data.role });
        setPermissionContext(data);
        setError(null);
      } else {
        console.error("[PermissionContext] Erreur chargement:", response.status);
        setError("Impossible de charger les permissions");
      }
    } catch (err) {
      console.error("[PermissionContext] Erreur:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.email, currentTenant]);

  // Recharger quand user ou tenant change
  useEffect(() => {
    loadPermissionContext();
  }, [loadPermissionContext]);

  // Titre d'onglet = "OmniHub - {TENANT}" (+ " - Preprod" en preprod).
  // Tenant DÉRIVÉ du contexte (currentTenant), env DÉRIVÉ du hostname — JAMAIS en dur :
  // un host sans "preprod" (prod) n'ajoute pas le suffixe → sûr à la promotion.
  // Se met à jour à chaque switch de tenant (currentTenant en dépendance). Avant
  // connexion (currentTenant null) → "OmniHub" (+ " - Preprod" en preprod).
  useEffect(() => {
    const slug =
      currentTenant?.slug ||
      currentTenant?.id ||
      (typeof currentTenant === "string" ? currentTenant : "");
    // Libellé court dérivé du slug : "omv_france" → "OMV", "tef" → "TEF".
    const tenantLabel = slug ? String(slug).split(/[_-]/)[0].toUpperCase() : "";
    const isPreprod = /preprod/i.test(window.location.hostname);
    let title = "OmniHub";
    if (tenantLabel) title += ` - ${tenantLabel}`;
    if (isPreprod) title += " - Preprod";
    document.title = title;
  }, [currentTenant]);

  // Écouter les changements de feature flags depuis RBACManager
  useEffect(() => {
    const handleFlagChange = () => {
      console.log("[PermissionContext] Feature flags changés, rechargement...");
      loadPermissionContext();
    };
    
    window.addEventListener('feature-flags-changed', handleFlagChange);
    return () => window.removeEventListener('feature-flags-changed', handleFlagChange);
  }, [loadPermissionContext]);

  // =========================================================================
  // HELPERS - Vérification des permissions
  // =========================================================================

  /**
   * Vérifie si un hub est activé (Feature Flag)
   */
  const isHubEnabled = useCallback((hubId) => {
    if (!permissionContext) return true; // Par défaut, tout est visible pendant le chargement
    
    // Super Admin voit tout
    if (permissionContext.is_super_admin) return true;
    
    // Vérifier dans les hubs actifs
    const hubs = permissionContext.hubs || {};
    return hubId in hubs && hubs[hubId]?.enabled !== false;
  }, [permissionContext]);

  /**
   * Vérifie si un module est activé (Feature Flag spécifique)
   */
  const isModuleEnabled = useCallback((hubId, moduleId) => {
    if (!permissionContext) return true;
    if (permissionContext.is_super_admin) return true;
    
    // Vérifier d'abord que le hub est activé
    if (!isHubEnabled(hubId)) return false;
    
    // Vérifier dans les modules du hub
    const hubs = permissionContext.hubs || {};
    const hub = hubs[hubId];
    if (!hub) return false;
    
    const modules = hub.modules || {};
    return moduleId in modules;
  }, [permissionContext, isHubEnabled]);

  /**
   * Vérifie si l'utilisateur a une permission spécifique
   */
  const hasPermission = useCallback((hubId, moduleId, action) => {
    if (!permissionContext) return true; // Par défaut pendant le chargement
    
    // Super Admin a toutes les permissions
    if (permissionContext.is_super_admin) return true;
    
    // Admin tenant a toutes les permissions sur son tenant
    if (permissionContext.permissions === "all" || permissionContext.permissions === "all_tenant") {
      return true;
    }
    
    // Vérifier d'abord que le hub et module sont activés
    if (!isHubEnabled(hubId) || !isModuleEnabled(hubId, moduleId)) {
      return false;
    }
    
    // Vérifier la permission spécifique
    const permissions = permissionContext.permissions || {};
    if (typeof permissions !== "object") return false;
    
    const hubPerms = permissions[hubId] || {};
    const modulePerms = hubPerms[moduleId] || [];
    
    return modulePerms.includes(action);
  }, [permissionContext, isHubEnabled, isModuleEnabled]);

  /**
   * Vérifie si une feature flag est activée
   */
  const isFeatureEnabled = useCallback((featureId) => {
    if (!permissionContext) return true;
    if (permissionContext.is_super_admin) return true;
    
    const flags = permissionContext.feature_flags || {};
    // Si non défini, retourner true par défaut (backward compatibility)
    return flags[featureId] !== false;
  }, [permissionContext]);

  /**
   * Retourne les hubs actifs (pour filtrer la navigation)
   */
  const getActiveHubs = useCallback(() => {
    if (!permissionContext) return [];
    
    const hubs = permissionContext.hubs || {};
    return Object.keys(hubs);
  }, [permissionContext]);

  /**
   * Retourne le rôle de l'utilisateur
   */
  const getUserRole = useCallback(() => {
    // Le backend renvoie `role` (sans accent) — lire `.rôle` rendait TOUJOURS "user".
    return permissionContext?.role || "user";
  }, [permissionContext]);

  /**
   * Vérifie si l'utilisateur est Super Admin
   */
  const isSuperAdmin = useCallback(() => {
    return permissionContext?.is_super_admin || false;
  }, [permissionContext]);

  /**
   * Vérifie si l'utilisateur est Admin (tenant ou super)
   */
  const isAdmin = useCallback(() => {
    const role = permissionContext?.role;
    return role === "super_admin" || role === "admin" || permissionContext?.is_super_admin;
  }, [permissionContext]);

  const value = {
    // État
    permissionContext,
    loading,
    error,
    
    // Méthodes de vérification
    isHubEnabled,
    isModuleEnabled,
    hasPermission,
    isFeatureEnabled,
    getActiveHubs,
    getUserRole,
    isSuperAdmin,
    isAdmin,
    
    // Actions
    reload: loadPermissionContext
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

/**
 * Hook pour utiliser les permissions
 */
export function usePermissions() {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error("usePermissions must be used within a PermissionProvider");
  }
  return context;
}

/**
 * Hook simplifié pour vérifier une permission spécifique
 * Usage: const canTransition = useCanDo("commercial", "dossiers", "transition")
 */
export function useCanDo(hub, module, action) {
  const { hasPermission, loading } = usePermissions();
  
  if (loading) return true; // Par défaut pendant le chargement
  return hasPermission(hub, module, action);
}

/**
 * Hook pour vérifier si un hub est visible
 * Usage: const showCommercial = useHubVisible("commercial")
 */
export function useHubVisible(hubId) {
  const { isHubEnabled, loading } = usePermissions();
  
  if (loading) return true;
  return isHubEnabled(hubId);
}

export default PermissionContext;
